// The web game's state: one engine GameSnapshot, driven through the engine's
// own handlers so settlement, drinks, jackpots and the bust rule work exactly
// as on the glasses. The engine's handlers are built around four gestures
// (move a cursor, tap, double-tap); a button press here is "put the cursor on
// that item, then tap".
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  handleBack,
  handleConfirm,
  handleMove,
  handleTick,
  hubItems,
  initialGameSnapshot,
  loadMeta,
  loadRun,
  saveMeta,
  saveRun,
  tableOptions,
  titleItems,
  type GameSnapshot,
} from './engine/casino';
import { GAMES } from './engine/games';
import type { GameId } from './engine/games/types';
import type { RunState } from './engine/casino';
import { PACHINKO } from './webGames/pachinko';

/** A table with a play still in progress (a hand dealt, a ticket half
 *  scratched, a rocket in the air), if there is one. */
export function unfinishedGame(run: RunState): GameId | null {
  for (const [id, slot] of Object.entries(run.tables) as [GameId, { state: unknown }][]) {
    if (slot?.state && GAMES[id]?.locked(slot.state)) return id;
  }
  return null;
}

/** One engine tick, the same pace as the glasses. */
const TICK_MS = 250;

/** Tables that draw their own animation on the web instead of stepping
 *  through the engine's lens frames. */
export const WEB_ANIMATED: readonly GameId[] = ['blackjack', 'slots', 'baccarat', 'highlow', 'liarsdice', 'mines', 'roulette', 'craps', 'threecard', 'holdem', 'bigsix', 'war', 'dice', 'videopoker', 'tower', 'plinko', 'horses', 'mahjong', PACHINKO];

export interface GameActions {
  titleItem(id: 'continue' | 'new' | 'reset'): void;
  /** On the "are you sure" screens: yes or no. */
  confirm(yes: boolean): void;
  openHubItem(index: number): void;
  tableOption(optionId: string): void;
  setBet(amount: number): void;
  /** End the current animation (a tap skips). */
  skip(): void;
  back(): void;
  /** Order the drink at this place on the Bar menu (a second tap confirms
   *  one that would black you out). */
  orderDrink(index: number): void;
  /** Buy the item at this place on the Gift Shop shelf (second tap buys). */
  buyItem(index: number): void;
  /** On the elevator: ride up, or stay. */
  ride(yes: boolean): void;
  /** After buying the casino: one last shopping trip, or finish. */
  ownerChoice(shop: boolean): void;
  /** The lens gestures, for games that take over the controls (Mines, Tower). */
  move(direction: 'up' | 'down'): void;
  tap(): void;
  /** Leave the bust or win screen. */
  dismiss(): void;
}

export function useGame(): [GameSnapshot, GameActions] {
  const [snapshot, setSnapshot] = useState(() => initialGameSnapshot(loadMeta(), loadRun()));
  const ref = useRef(snapshot);

  const commit = useCallback((step: (s: GameSnapshot) => GameSnapshot) => {
    const next = step(ref.current);
    const prev = ref.current;
    if (next === prev) return;
    ref.current = next;
    // Only what changed: cursor moves and animation frames keep the same run
    // and meta, so they don't rewrite the saves.
    if (next.meta !== prev.meta) saveMeta(next.meta);
    if (next.run !== prev.run) saveRun(next.run);
    setSnapshot(next);
  }, []);

  // Real-time games (Crash, AI turns) and the lens-frame animations of tables
  // without a web animation advance on the engine's tick.
  useEffect(() => {
    const timer = setInterval(() => {
      const s = ref.current;
      if (s.screen !== 'table') return;
      if (s.anim && s.activeGame && WEB_ANIMATED.includes(s.activeGame)) return;
      commit((x) => handleTick(x, Date.now()));
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [commit]);

  const [actions] = useState<GameActions>(() => ({
    titleItem: (id) =>
      commit((s) => {
        const index = titleItems(s).findIndex((i) => i.id === id);
        if (index < 0) return s;
        const next = handleConfirm({ ...s, titleCursor: index });
        // Continue goes straight back into a play left unfinished (user,
        // 2026-09-27): the engine opens the floor, but a hand in progress
        // should pick up where it was.
        const open = id === 'continue' && next.screen === 'hub' && next.run ? unfinishedGame(next.run) : null;
        return open ? { ...next, screen: 'table', activeGame: open, tableCursor: 0 } : next;
      }),
    confirm: (yes) => commit((s) => handleConfirm({ ...s, titleCursor: yes ? 0 : 1 })),
    openHubItem: (index) => commit((s) => (s.run && index < hubItems(s.run).length ? handleConfirm({ ...s, hubCursor: index }) : s)),
    tableOption: (optionId) =>
      commit((s) => {
        const index = tableOptions(s).findIndex((o) => o.id === optionId);
        return index < 0 ? s : handleConfirm({ ...s, tableCursor: index });
      }),
    setBet: (amount) =>
      commit((s) => {
        const index = tableOptions(s).findIndex((o) => o.id === 'bet');
        if (index < 0) return s;
        const editing = handleConfirm({ ...s, tableCursor: index });
        return editing.screen === 'betEdit' ? handleConfirm({ ...editing, pendingBet: amount }) : s;
      }),
    skip: () => commit((s) => (s.anim ? handleConfirm(s) : s)),
    back: () => commit(handleBack),
    orderDrink: (index) =>
      commit((s) => handleConfirm({ ...s, barCursor: index, barConfirm: s.barCursor === index ? s.barConfirm : null })),
    buyItem: (index) =>
      commit((s) => handleConfirm({ ...s, shopCursor: index, shopConfirm: s.shopCursor === index ? s.shopConfirm : null })),
    ride: (yes) => commit((s) => (s.screen === 'elevator' ? handleConfirm({ ...s, elevatorCursor: yes ? 0 : 1 }) : s)),
    ownerChoice: (shop) => commit((s) => (s.screen === 'owner' ? handleConfirm({ ...s, ownerCursor: shop ? 0 : 1 }) : s)),
    move: (direction) => commit((s) => handleMove(s, direction)),
    tap: () => commit(handleConfirm),
    dismiss: () => commit((s) => (s.screen === 'bust' ? handleConfirm(s) : s)),
  }));

  return [snapshot, actions];
}
